import { ImgHTMLAttributes, useEffect, useRef } from 'react'

import tools from '../../utils/tools'

const CDN_HOSTS: Record<string, string> = {
  'cdn-fotos': 'https://d2pqkiqr9ld81d.cloudfront.net',
  'cdn-s3': 'https://d3hiabse0z0zpx.cloudfront.net'
}

const CDN_TIMEOUT_MS = 1000

function getImageCandidates(src: string): string[] {
  let url: URL
  try {
    url = new URL(src)
  } catch {
    return [src]
  }

  if (!/^https?:$/i.test(url.protocol)) return [src]

  const cloudfrontOrigin = Object.entries(CDN_HOSTS).find(([hostname]) => url.hostname.startsWith(hostname))?.[1]

  if (!cloudfrontOrigin) return [src]

  const cloudfrontUrl = new URL(cloudfrontOrigin)
  cloudfrontUrl.pathname = url.pathname
  cloudfrontUrl.search = url.search
  cloudfrontUrl.hash = url.hash

  return [src, cloudfrontUrl.toString()]
}

function loadImage(candidate: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    const timeout = setTimeout(() => {
      image.onload = null
      image.onerror = null
      reject(new Error(`Timed out loading image: ${candidate}`))
    }, CDN_TIMEOUT_MS)

    image.onload = () => {
      clearTimeout(timeout)
      resolve(candidate)
    }
    image.onerror = () => {
      clearTimeout(timeout)
      reject(new Error(`Unable to load image: ${candidate}`))
    }
    image.src = candidate
  })
}

export async function loadImageWithCdnFallback(src: string): Promise<string> {
  const candidates = getImageCandidates(src)
  let lastError: Error | undefined

  for (const candidate of candidates) {
    try {
      return await loadImage(candidate)
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error))
    }
  }

  throw lastError || new Error(`Unable to load image: ${src}`)
}

export const LazyImage: React.FC<ImgHTMLAttributes<HTMLImageElement>> = ({ src, ...props }) => {
  const loaderRef = useRef<HTMLImageElement>(null)
  const observer = useRef<IntersectionObserver>()

  useEffect(() => {
    if (!src) return

    let active = true

    const loadImage = () => {
      if (!active) return
      const image = loaderRef.current
      if (!image || image.src === src) return

      loadImageWithCdnFallback(src)
        .then(loadedSrc => {
          if (active && loaderRef.current) loaderRef.current.src = loadedSrc
        })
        .catch(() => {
          if (active && loaderRef.current) {
            tools.imagemOnError({ target: loaderRef.current } as Parameters<typeof tools.imagemOnError>[0])
          }
        })
    }

    observer.current = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        if (entries.some(entry => entry.isIntersecting)) {
          loadImage()
          if (loaderRef.current) {
            observer.current?.unobserve(loaderRef.current)
          }
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.3
      }
    )
    if (loaderRef.current) {
      observer.current.observe(loaderRef.current)
    }

    return () => {
      active = false
      observer.current?.disconnect()
    }
  }, [src])

  return <img onError={tools.imagemOnError} {...props} ref={loaderRef} />
}
